<template>
  <div class="sit">
    <span class="form-label-sm d-block" :id="'sit-label-' + productId">Supplies these standard items</span>
    <small class="text-muted d-block mb-2">
      Facilities whose assessments find these items missing see this product as an offer.
    </small>
    <div v-if="error" class="alert alert-danger small py-1">{{ error }}</div>

    <!-- Vendor: pick items; admin-set tags are shown but fixed -->
    <template v-if="!admin">
      <div v-if="locked.length" class="mb-2">
        <span v-for="t in locked" :key="t.standard_item_id" class="sit-chip sit-chip--locked" title="Set by the marketplace team">
          <i class="fe fe-lock"></i> {{ t.name }}
        </span>
      </div>
      <v-select v-model="selected" :options="choices" label="name" :reduce="i => i.id" multiple
        :aria-labelledby="'sit-label-' + productId" placeholder="Search standard items…" />
    </template>

    <!-- Platform admin: every tag, with a block switch -->
    <template v-else>
      <div v-for="t in adminTags" :key="t.standard_item_id" class="sit-row">
        <span class="flex-fill">{{ t.name }} <span class="small text-muted">({{ t.source }})</span></span>
        <div class="custom-control custom-switch">
          <input :id="`sit-b-${productId}-${t.standard_item_id}`" v-model="t.blocked" type="checkbox" class="custom-control-input" />
          <label class="custom-control-label small" :for="`sit-b-${productId}-${t.standard_item_id}`">Never offer for this item</label>
        </div>
        <button type="button" class="btn btn-sm btn-link text-danger" :aria-label="'Remove ' + t.name" @click="removeAdminTag(t)"><i class="fe fe-x"></i></button>
      </div>
      <v-select :value="null" :options="adminChoices" label="name" :aria-labelledby="'sit-label-' + productId"
        placeholder="Add a standard item…" @input="addAdminTag" />
    </template>

    <button type="button" class="btn btn-sm btn-outline-primary mt-2" :disabled="saving" @click="save">Save standard items</button>
  </div>
</template>

<script>
import { API, errorText } from "./assessments";

let itemsCache = null;

export default {
  name: "StandardItemTags",
  props: {
    productId: { type: [Number, String], required: true },
    admin: { type: Boolean, default: false },
  },
  data() {
    return { items: [], tags: [], selected: [], adminTags: [], saving: false, error: "" };
  },
  computed: {
    locked() {
      return this.tags.filter((t) => t.locked && !Number(t.is_blocked));
    },
    choices() {
      const locked = this.locked.map((t) => t.standard_item_id);
      return this.items.filter((i) => !locked.includes(i.id));
    },
    adminChoices() {
      const used = this.adminTags.map((t) => t.standard_item_id);
      return this.items.filter((i) => !used.includes(i.id));
    },
  },
  watch: {
    productId() {
      this.loadTags();
    },
  },
  created() {
    (itemsCache || (itemsCache = this.axios.get(`${API}/standard-items`).then((r) => r.data.data || [])))
      .then((items) => (this.items = items))
      .catch(() => { itemsCache = null; });
    this.loadTags();
  },
  methods: {
    apply(tags) {
      this.tags = tags;
      this.selected = tags.filter((t) => !t.locked).map((t) => t.standard_item_id);
      this.adminTags = tags.map((t) => ({ standard_item_id: t.standard_item_id, name: t.name, source: t.source, blocked: !!Number(t.is_blocked) }));
    },
    loadTags() {
      this.error = "";
      this.axios
        .get(`/api/v1/auth/vendor/products/${this.productId}/standard-items`)
        .then((res) => this.apply(res.data.data || []))
        .catch((err) => (this.error = errorText(err, "Could not load the standard items.")));
    },
    addAdminTag(item) {
      if (item) this.adminTags.push({ standard_item_id: item.id, name: item.name, source: "admin", blocked: false });
    },
    removeAdminTag(t) {
      this.adminTags = this.adminTags.filter((x) => x !== t);
    },
    save() {
      this.saving = true;
      this.error = "";
      const request = this.admin
        ? this.axios.put(`/api/v1/auth/marketplace/admin/products/${this.productId}/standard-items`, {
          tags: this.adminTags.map((t) => ({ standard_item_id: t.standard_item_id, blocked: t.blocked })),
        })
        : this.axios.put(`/api/v1/auth/vendor/products/${this.productId}/standard-items`, { standard_item_ids: this.selected });
      request
        .then((res) => {
          this.apply(res.data.data || []);
          this.$toasted.success("Standard items saved", { position: "top-center", duration: 3000 });
        })
        .catch((err) => (this.error = errorText(err, "Could not save.")))
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.sit { border-top: 1px solid #f1f5f9; padding-top: 12px; margin-bottom: 14px; }
.sit-chip { display: inline-flex; align-items: center; gap: 4px; background: #e0e7ff; color: #3730a3; border-radius: 12px; padding: 2px 10px; font-size: .78rem; margin: 0 6px 6px 0; }
.sit-row { display: flex; align-items: center; gap: 10px; padding: 4px 0; border-bottom: 1px solid #f8fafc; font-size: .88rem; }
</style>
